import moment from 'moment';
import { FC, useMemo, useState } from 'react';

import * as graphql from '@treams/treams-network';
import { ITimeMachine } from '@treams/treams-ui/common/DateIntervalNavigation';
import { SidebarLayout } from '@treams/treams-ui/common/SidebarLayout';

import { GrowthPathData } from '../GrowthPathConverter';
import { GrowthPathReceivedFeedbackList } from './GrowthPathCompetenciesList';
import GrowthPathDetailBody from './GrowthPathDetailBody';
import GrowthPathOverviewSidebar, { GrowthPathOverviewGiver } from './GrowthPathOverviewSidebar';

type Props = {
	data: GrowthPathData;
	feedback: GrowthPathReceivedFeedbackList;
	timeMachine: ITimeMachine;
	onTimeMachineChange: (timeMachine: ITimeMachine) => void;
};

function inSelectedPeriod(score: graphql.CompetencyFeedbackScore, timeMachine: ITimeMachine) {
	return moment(score.timestamp).isBetween(
		timeMachine.current[0],
		timeMachine.current[1],
		undefined,
		'[]',
	);
}

function filterPeriodScores(
	scores: graphql.CompetencyFeedbackScore[] | null,
	timeMachine: ITimeMachine,
): graphql.CompetencyFeedbackScore[] | null {
	if (scores === null) {
		return null;
	}

	return scores.filter((score) => inSelectedPeriod(score, timeMachine));
}

function filterGiverScores(
	scores: graphql.CompetencyFeedbackScore[] | null,
	selectedGiverId: string,
): graphql.CompetencyFeedbackScore[] | null {
	if (scores === null) {
		return null;
	}

	return scores.filter((score) => giverKey(score.giver) === selectedGiverId);
}

function giverKey(giver: graphql.CompetencyFeedbackScore['giver']): string {
	if ('id' in giver && giver.id) {
		return giver.id;
	}

	return giver.email;
}

const GrowthPathAllCompetencies: FC<Props> = ({
	data,
	feedback,
	timeMachine,
	onTimeMachineChange,
}) => {
	const [selectedGiverId, setSelectedGiverId] = useState<string | null>(null);

	const periodFeedback = useMemo<GrowthPathReceivedFeedbackList>(() => {
		return {
			scoresByJobCompetencyId: Object.fromEntries(
				Object.entries(feedback.scoresByJobCompetencyId).map(([jobCompetencyId, scores]) => [
					jobCompetencyId,
					filterPeriodScores(scores, timeMachine),
				]),
			),
		};
	}, [feedback, timeMachine]);

	const givers = useMemo(() => {
		const byId = new Map<string, GrowthPathOverviewGiver>();

		for (const scores of Object.values(periodFeedback.scoresByJobCompetencyId)) {
			if (!scores) {
				continue;
			}

			for (const score of scores) {
				const id = giverKey(score.giver);
				if (byId.has(id)) {
					continue;
				}

				byId.set(id, {
					id,
					firstName: score.giver.firstName,
					lastName: score.giver.lastName,
					email: 'email' in score.giver ? score.giver.email : undefined,
				});
			}
		}

		return [...byId.values()];
	}, [periodFeedback.scoresByJobCompetencyId]);

	const visibleFeedback = useMemo<GrowthPathReceivedFeedbackList>(() => {
		if (!selectedGiverId) {
			return periodFeedback;
		}

		const scoresByJobCompetencyId = Object.fromEntries(
			Object.entries(periodFeedback.scoresByJobCompetencyId).map(([jobCompetencyId, scores]) => [
				jobCompetencyId,
				filterGiverScores(scores, selectedGiverId),
			]),
		);

		return { scoresByJobCompetencyId };
	}, [periodFeedback, selectedGiverId]);

	return (
		<SidebarLayout
			right
			sidebar={
				<GrowthPathOverviewSidebar
					givers={givers}
					selectedGiverId={selectedGiverId}
					onGiverChange={setSelectedGiverId}
					timeMachine={timeMachine}
					onTimeMachineChange={onTimeMachineChange}
				/>
			}
		>
			<GrowthPathDetailBody data={data} receivedFeedback={visibleFeedback} />
		</SidebarLayout>
	);
};

export default GrowthPathAllCompetencies;
